import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Loader2,
  Database,
  AlertTriangle,
  X
} from 'lucide-react';
import { storeService } from '../../services/store';

interface SyncJob {
  id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  total_products: number | null;
  processed_products: number | null;
  current_page: number | null;
  progress_percentage: number | null;
  error_message?: string;
  estimated_remaining_minutes?: number;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  // Category sync fields
  sync_mode?: string;
  current_category_index?: number | null;
  current_category_id?: number | null;
  current_category_page?: number | null;
  category_products_processed?: number | null;
  total_categories?: number | null;
  current_category_name?: string | null;
}

interface SyncProgressProps {
  storeId: string;
  onSyncComplete?: () => void;
  onClose?: () => void;
  onSyncCancelled?: () => void;
}

export function SyncProgress({ storeId, onSyncComplete, onClose, onSyncCancelled }: SyncProgressProps) {
  const [syncJob, setSyncJob] = useState<SyncJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [pollStartTime, setPollStartTime] = useState<Date | null>(null);
  const [waitingTime, setWaitingTime] = useState(0);

  // Load persistent state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem(`sync-progress-${storeId}`);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        setStartTime(parsed.startTime);
        setSyncJob(parsed.syncJob);
      } catch (e) {
        console.error('Failed to load sync state from localStorage:', e);
      }
    }
    loadSyncStatus();
    setPollStartTime(new Date()); // Track when polling started
  }, [storeId]);

  useEffect(() => {
    // Start polling if sync is active OR if no sync job is found yet (waiting for sync to start)
    const shouldPoll = 
      (syncJob && ['pending', 'in_progress'].includes(syncJob.status)) ||
      (!syncJob && !error && pollStartTime && (Date.now() - pollStartTime.getTime()) < 60000); // Poll for max 60 seconds when waiting
    
    if (shouldPoll) {
      const pollInterval = setInterval(() => {
        setPolling(true);
        loadSyncStatus();
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(pollInterval);
    }
  }, [syncJob?.status, error, pollStartTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update elapsed time every second
  useEffect(() => {
    if (syncJob && syncJob.status === 'in_progress' && (syncJob.started_at || startTime)) {
      const timer = setInterval(() => {
        const start = new Date(syncJob.started_at || startTime || '').getTime();
        const now = Date.now();
        setElapsedSeconds(Math.floor((now - start) / 1000));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [syncJob?.status, syncJob?.started_at, startTime]);

  // Update waiting time every second when no sync job is found
  useEffect(() => {
    if (!syncJob && pollStartTime) {
      const timer = setInterval(() => {
        setWaitingTime(Math.floor((Date.now() - pollStartTime.getTime()) / 1000));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [syncJob, pollStartTime]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (syncJob && syncJob.status === 'in_progress') {
      const stateToSave = {
        syncJob,
        startTime: syncJob.started_at || startTime || new Date().toISOString(),
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(`sync-progress-${storeId}`, JSON.stringify(stateToSave));
    } else if (syncJob && ['completed', 'failed', 'cancelled'].includes(syncJob.status)) {
      // Clear localStorage when sync is done
      localStorage.removeItem(`sync-progress-${storeId}`);
    }
  }, [syncJob, storeId, startTime]);

  const getUserFriendlyErrorMessage = (error: string) => {
    // Check if it's a resume-friendly timeout message
    if (error.includes('Timeout after') && error.includes('Resume sync to continue')) {
      return error; // Return the exact message as it's already user-friendly
    }
    // Convert technical errors to user-friendly messages
    if (error.includes('fetch') || error.includes('network') || error.includes('ECONNREFUSED')) {
      return 'Unable to connect to the sync service. Please try again later.';
    }
    if (error.includes('timeout')) {
      return 'The sync service is taking longer than expected. You can resume the sync to continue.';
    }
    if (error.includes('authentication') || error.includes('unauthorized')) {
      return 'Authentication issue. Please refresh the page and try again.';
    }
    if (error.includes('not found') || error.includes('404')) {
      return 'Sync job not found. It may have been completed or cancelled.';
    }
    if (error.includes('500')) {
      return 'Server error occurred. Please try again or contact support.';
    }
    if (error.includes('503')) {
      return 'Sync service is temporarily unavailable. Please try again later.';
    }
    // Default user-friendly message with more details for debugging
    return `Unable to load sync status: ${error}. Please try refreshing.`;
  };

  const loadSyncStatus = async () => {
    try {
      setError(null);
      if (!polling) setLoading(true);
      
      const response = await storeService.getSyncStatus(storeId);
      
      // Handle case where sync_job is null
      if (!response || !response.sync_job) {
        // Don't treat this as an error - sync might not have started yet
        // or might have completed and been cleaned up
        setSyncJob(null);
      } else {
        setSyncJob(response.sync_job);
        
        // Call onSyncComplete when sync finishes successfully
        if (response.sync_job.status === 'completed' && onSyncComplete) {
          onSyncComplete();
        }
      }
    } catch (error: any) {
      console.error('Sync status error:', error);
      const friendlyMessage = getUserFriendlyErrorMessage(error.message || 'Unknown error');
      setError(friendlyMessage);
      // Ensure syncJob is set to null on error to prevent undefined access
      setSyncJob(null);
    } finally {
      setLoading(false);
      setPolling(false);
    }
  };

  const handleCancelSync = async () => {
    try {
      await storeService.cancelSync(storeId);
      await loadSyncStatus(); // Refresh status
      if (onSyncCancelled) {
        onSyncCancelled();
      }
    } catch (error: any) {
      const friendlyMessage = getUserFriendlyErrorMessage(error.message || 'Failed to cancel sync');
      setError(friendlyMessage);
    }
  };

  const handleResumeSync = async () => {
    if (!syncJob) return;
    
    try {
      // Call resume endpoint through the store service
      await storeService.resumeSync(storeId);
      await loadSyncStatus(); // Refresh status
    } catch (error: any) {
      const friendlyMessage = getUserFriendlyErrorMessage(error.message || 'Failed to resume sync');
      setError(friendlyMessage);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge className="bg-warning/10 text-warning border-warning/20">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge className="bg-primary/10 text-primary border-primary/20">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            In Progress
          </Badge>
        );
      case 'completed':
        return (
          <Badge className="bg-success/10 text-success border-success/20">
            <CheckCircle className="w-3 h-3 mr-1" />
            Completed
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-destructive/10 text-destructive border-destructive/20">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge className="bg-muted text-muted-foreground border-border">
            <X className="w-3 h-3 mr-1" />
            Cancelled
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            {status}
          </Badge>
        );
    }
  };

  const formatTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatElapsedTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const calculateEstimatedTime = () => {
    if (!syncJob || !syncJob.processed_products || !syncJob.total_products || syncJob.processed_products === 0) {
      return null;
    }

    // Calculate processing rate (products per second)
    const processingRate = syncJob.processed_products / elapsedSeconds;
    
    // Calculate remaining products
    const remainingProducts = syncJob.total_products - syncJob.processed_products;
    
    // Calculate estimated seconds remaining
    const estimatedSecondsRemaining = Math.ceil(remainingProducts / processingRate);
    
    // Convert to minutes for display
    const estimatedMinutesRemaining = Math.ceil(estimatedSecondsRemaining / 60);
    
    return {
      minutes: estimatedMinutesRemaining,
      formatted: formatDuration(estimatedMinutesRemaining),
      totalEstimatedMinutes: Math.ceil((syncJob.total_products / processingRate) / 60),
      processingRate: processingRate.toFixed(2)
    };
  };

  if (loading && !syncJob) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin mr-2" />
          <span>Loading sync status...</span>
        </CardContent>
      </Card>
    );
  }

  if (error && !syncJob) {
    return (
      <Card>
        <CardContent className="py-6">
          <Alert className="border-destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="flex justify-between mt-4">
            <Button variant="outline" onClick={loadSyncStatus}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
            {onClose && (
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!syncJob) {
    const isTimeout = waitingTime > 60;
    
    return (
      <Card>
        <CardContent className="py-6">
          <Alert variant={isTimeout ? "destructive" : "default"}>
            {isTimeout ? (
              <AlertTriangle className="h-4 w-4" />
            ) : (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            <AlertDescription>
              <div className="space-y-2">
                {isTimeout ? (
                  <>
                    <p>Sync job not found after {waitingTime} seconds</p>
                    <p className="text-sm text-muted-foreground">
                      The sync might have failed to start. Please try again or check the logs.
                    </p>
                  </>
                ) : (
                  <>
                    <p>Waiting for sync job to start... ({waitingTime}s)</p>
                    <p className="text-sm text-muted-foreground">
                      The sync job has been created and will begin processing shortly.
                    </p>
                  </>
                )}
              </div>
            </AlertDescription>
          </Alert>
          <div className="flex justify-center mt-4">
            <div className="text-xs text-muted-foreground animate-pulse">
              {isTimeout ? 'Stopped polling after 60 seconds' : 'Checking status every 2 seconds...'}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isActive = ['pending', 'in_progress'].includes(syncJob.status);
  const canCancel = isActive;
  const canResume = syncJob.status === 'failed' && (syncJob.processed_products || 0) > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center">
            <Database className="h-5 w-5 mr-2" />
            Product Sync Progress
          </CardTitle>
          <div className="flex items-center gap-2">
            {getStatusBadge(syncJob.status)}
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Progress Section */}
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span>Products Processed</span>
            <span className="font-mono">
              {(syncJob.processed_products || 0).toLocaleString()} / {
                syncJob.total_products !== null 
                  ? syncJob.total_products.toLocaleString() 
                  : 'calculating...'
              }
              {syncJob.total_products !== null && ` (${syncJob.progress_percentage || 0}%)`}
            </span>
          </div>
          
          <Progress 
            value={syncJob.total_products !== null ? (syncJob.progress_percentage || 0) : 0} 
            className="h-3" 
          />
          
          {syncJob.total_products === null && (
            <div className="text-center text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Database className="h-3 w-3 animate-pulse" />
                Calculating total products...
              </span>
            </div>
          )}
          
          {syncJob.status === 'in_progress' && (
            <div className="space-y-3">
              {/* Time Estimates Card */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Elapsed Time:</span>
                    <p className="font-semibold text-foreground">{formatElapsedTime(elapsedSeconds)}</p>
                  </div>
                  {calculateEstimatedTime() && (
                    <div>
                      <span className="text-muted-foreground">Est. Remaining:</span>
                      <p className="font-semibold text-foreground">{calculateEstimatedTime()?.formatted}</p>
                    </div>
                  )}
                </div>
                
                {calculateEstimatedTime() && (
                  <div className="pt-2 border-t border-border/50">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Processing Rate:</span>
                      <span className="font-mono">{calculateEstimatedTime()?.processingRate} products/sec</span>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>Est. Total Time:</span>
                      <span className="font-mono">{formatDuration(calculateEstimatedTime()?.totalEstimatedMinutes)}</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Current Status - Show category info for category sync, page info otherwise */}
              {syncJob.sync_mode === 'category' && syncJob.total_categories ? (
                <div className="space-y-2">
                  {/* Category Progress */}
                  <div className="bg-primary/5 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Processing Category:</span>
                      <span className="font-semibold text-foreground">
                        {syncJob.current_category_name || 'Loading...'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Category:</span>
                      <span className="font-mono text-foreground">
                        {(syncJob.current_category_index || 0) + 1} of {syncJob.total_categories}
                      </span>
                    </div>
                    {syncJob.category_products_processed && syncJob.category_products_processed > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Products in Category:</span>
                        <span className="font-mono text-foreground">
                          {syncJob.category_products_processed} processed
                        </span>
                      </div>
                    )}
                    
                    {/* Category Progress Bar */}
                    <div className="pt-2">
                      <Progress 
                        value={((syncJob.current_category_index || 0) + 1) / syncJob.total_categories * 100} 
                        className="h-2" 
                      />
                    </div>
                  </div>
                  
                  {/* Category-specific page info */}
                  <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                    <div>
                      <span className="font-medium">Category Page:</span> {syncJob.current_category_page || 'N/A'}
                    </div>
                    <div>
                      <span className="font-medium">Overall Progress:</span> {
                        syncJob.total_products !== null 
                          ? `${Math.round((syncJob.processed_products || 0) / syncJob.total_products * 100)}%`
                          : 'calculating...'
                      }
                    </div>
                  </div>
                </div>
              ) : (
                /* Regular page-based sync status */
                <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                  <div>
                    <span className="font-medium">Current Page:</span> {syncJob.current_page || 'N/A'}
                  </div>
                  <div>
                    <span className="font-medium">Total Pages:</span> {
                      syncJob.total_products !== null 
                        ? Math.ceil(syncJob.total_products / 200)
                        : 'calculating...'
                    }
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Message with Resume Info */}
        {syncJob.error_message && (
          <Alert className="border-destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p>{getUserFriendlyErrorMessage(syncJob.error_message)}</p>
                {(syncJob.processed_products || 0) > 0 && (
                  <p className="text-sm text-muted-foreground">
                    ✅ Progress saved: {syncJob.processed_products} products processed. 
                    You can resume from page {syncJob.current_page} without losing progress.
                  </p>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Status Information */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-muted-foreground">Started:</span>
            <p className="text-xs">{formatTime(syncJob.started_at)}</p>
          </div>
          
          {syncJob.completed_at && (
            <div>
              <span className="font-medium text-muted-foreground">Completed:</span>
              <p className="text-xs">{formatTime(syncJob.completed_at)}</p>
            </div>
          )}
          
          <div>
            <span className="font-medium text-muted-foreground">Job ID:</span>
            <p className="text-xs font-mono">{syncJob.id}</p>
          </div>
          
        </div>

        {/* Actions */}
        <div className="flex justify-between pt-4 border-t">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadSyncStatus}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            
            {canCancel && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleCancelSync}
                className="text-destructive hover:text-destructive"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel Sync
              </Button>
            )}
            
            {canResume && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleResumeSync}
                className="text-primary hover:text-primary"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {syncJob.sync_mode === 'category' && syncJob.current_category_name
                  ? `Resume from ${syncJob.current_category_name}`
                  : `Resume from Page ${syncJob.current_page}`
                }
              </Button>
            )}
          </div>
          
          {syncJob.status === 'completed' && onSyncComplete && (
            <Button variant="outline" size="sm" onClick={onSyncComplete}>
              <CheckCircle className="h-4 w-4 mr-2" />
              View Products
            </Button>
          )}
        </div>

        {/* Sync Status */}
        {syncJob.status === 'pending' && (
          <Alert variant="warning" className="bg-warning/10">
            <Database className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-warning rounded-full mr-2 animate-pulse"></div>
                {syncJob.sync_mode === 'category' 
                  ? 'Category sync queued - Processing will begin shortly'
                  : 'Sync queued - Processing will begin shortly'
                }
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Success Status */}
        {syncJob.status === 'completed' && (
          <Alert variant="success">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              {syncJob.sync_mode === 'category' && syncJob.total_categories
                ? `Category sync completed successfully! Processed ${syncJob.processed_products} unique products across ${syncJob.total_categories} categories.`
                : 'Sync completed successfully! All products have been processed.'
              }
            </AlertDescription>
          </Alert>
        )}

        {/* Robust Sync Features */}
        {isActive && (
          <div className="space-y-2">
            <div className="flex items-center justify-center text-xs text-muted-foreground">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse mr-2"></div>
              Live updates every 2 seconds
            </div>
            <div className="text-center text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Database className="h-3 w-3" />
                Progress is saved - you can safely leave and return to this page
              </span>
            </div>
            <div className="text-center text-xs text-blue-600">
              <span className="inline-flex items-center gap-1">
                <RefreshCw className="h-3 w-3" />
                {syncJob.sync_mode === 'category' 
                  ? 'Category-optimized sync • Automatic category processing • Resume on failure'
                  : 'Auto-retry on timeouts • 200 products per batch • Resume on failure'
                }
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}